import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, PlayCircle, GraduationCap, Briefcase, Download, HeartHandshake, Sparkles, Quote, Play, ChevronDown, RotateCcw, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Overline, SectionHeading, GoldRule } from "@/components/Bits";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import NewsletterSignup from "@/components/NewsletterSignup";
import HomeStoriesOfFaith from "@/components/HomeStoriesOfFaith";
import { PORTRAIT, HERO_PORTRAIT } from "@/lib/assets";
import { currentLang } from "@/lib/lang";

const MOODS = [
  "Anxious", "Grateful", "Discouraged", "Joyful",
  "Weary", "Broken", "Hopeful", "Angry",
  "Lonely", "Confused", "At peace", "Overwhelmed",
];

const CTAS = [
  { label: "Watch Messages", to: "/watch", icon: PlayCircle },
  { label: "Join the AI Academy", to: "/academy", icon: GraduationCap },
  { label: "Book Consulting", to: "/consulting", icon: Briefcase },
  { label: "Free Resources", to: "/resources", icon: Download },
];

const PILLARS = [
  { n: "01", title: "Faith", desc: "Proclaiming the uncompromised Word of God and raising mature disciples.", to: "/ministry" },
  { n: "02", title: "Leadership", desc: "Mentoring executives, entrepreneurs, and emerging leaders into excellence.", to: "/about" },
  { n: "03", title: "Business", desc: "Ethical strategy and consulting rooted in Kingdom principles.", to: "/consulting" },
  { n: "04", title: "Innovation", desc: "Equipping leaders for the digital age through the Judah AI Academy.", to: "/academy" },
];

// ---------------------------------------------------------------------------
// Mood-Responsive Guide — top-of-page hero
// ---------------------------------------------------------------------------
function MoodHero() {
  const [mood, setMood] = useState("");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const resultRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (mood.trim().length < 2) {
      toast.error("Please choose or type how you're feeling.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/mood-guide", { mood: mood.trim(), context: context.trim(), language: currentLang() });
      setResult(data);
      // Scroll gently to the response after paint
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setMood("");
    setContext("");
    setShowContext(false);
    document.getElementById("mood-hero")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="mood-hero"
      data-testid="mood-hero"
      className="relative overflow-hidden bg-ink"
    >
      {/* Ambient royal-blue radial glow — calm, spacious */}
      <div className="pointer-events-none absolute inset-0 opacity-90">
        <div className="absolute -top-40 -left-40 h-[820px] w-[820px] rounded-full bg-ink-royal/60 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[520px] w-[520px] rounded-full bg-gold-500/8 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28">
        <div className="grid gap-10 lg:gap-16 lg:grid-cols-12 items-center">

          {/* -------- Left column · Portrait sidebar -------- */}
          <motion.aside
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 xl:col-span-5 order-1"
            data-testid="mood-hero-portrait"
          >
            <div className="relative mx-auto max-w-[460px] lg:max-w-none">
              {/* Corner ornaments — kingdom crest touch */}
              <span className="absolute -top-3 -left-3 w-8 h-8 border-t border-l border-gold-500/60 z-10" />
              <span className="absolute -top-3 -right-3 w-8 h-8 border-t border-r border-gold-500/60 z-10" />
              <span className="absolute -bottom-3 -left-3 w-8 h-8 border-b border-l border-gold-500/60 z-10" />
              <span className="absolute -bottom-3 -right-3 w-8 h-8 border-b border-r border-gold-500/60 z-10" />

              {/* Portrait frame */}
              <div className="relative aspect-[4/5] overflow-hidden border border-gold-500/30 shadow-[0_60px_140px_-40px_rgba(11,32,83,0.9)] bg-ink-royal">
                <img
                  src={HERO_PORTRAIT}
                  alt="Apostle Blaise Judah — Visionary Leader, Educator, Kingdom Builder"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  loading="eager"
                  fetchPriority="high"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
              </div>
            </div>
          </motion.aside>

          {/* -------- Right column · Mood-Responsive Guide -------- */}
          <div className="lg:col-span-7 xl:col-span-7 order-2">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 border border-gold-500/25 px-4 py-1.5 rounded-full mb-6">
                <HeartHandshake size={13} className="text-gold-400" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-gold-300">Mood-Responsive Guide</span>
              </div>

              <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight">
                How are you <span className="font-accent text-gold-gradient italic">feeling</span> today?
              </h1>
              <p className="mt-5 md:mt-6 text-white/65 text-base md:text-lg leading-relaxed max-w-xl">
                Choose a mood below. In seconds, receive a scripture, a word of encouragement, and a prayer written for this exact moment.
              </p>
            </motion.div>

            {/* Mood grid — 3 cols on all sizes for a tidy sidebar */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.9 }}
              className="mt-9 md:mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3"
              data-testid="mood-hero-chips"
            >
              {MOODS.map((m) => {
                const active = mood.toLowerCase() === m.toLowerCase();
                return (
                  <motion.button
                    key={m}
                    type="button"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setMood(m)}
                    data-testid={`mood-hero-chip-${m.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative px-4 py-3.5 md:py-4 text-sm md:text-[15px] font-medium tracking-wide border transition-colors duration-300 rounded-none ${
                      active
                        ? "border-gold-400 bg-gold-500/12 text-gold-200 shadow-[0_0_0_1px_rgba(212,175,55,0.35)_inset]"
                        : "border-white/12 bg-ink-paper/40 text-white/75 hover:border-gold-500/50 hover:text-white hover:bg-ink-paper/60"
                    }`}
                  >
                    {m}
                    {active && (
                      <motion.span
                        layoutId="mood-underline"
                        className="absolute left-4 right-4 bottom-1.5 h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Optional context + submit */}
            <motion.form
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.9 }}
              onSubmit={submit}
              className="mt-8 md:mt-10"
              data-testid="mood-hero-form"
            >
              <input
                type="text"
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                placeholder="…or type how you feel in your own words"
                data-testid="mood-hero-input"
                className="w-full bg-ink-paper/60 border border-white/15 focus:border-gold-500/60 text-white placeholder:text-white/40 px-6 py-4 text-[15px] outline-none transition-colors rounded-none"
              />

              {/* Collapsible context — keeps hero calm */}
              <button
                type="button"
                onClick={() => setShowContext((v) => !v)}
                data-testid="mood-hero-context-toggle"
                className="mt-4 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/45 hover:text-gold-300 transition-colors"
              >
                <ChevronDown size={12} className={`transition-transform duration-300 ${showContext ? "rotate-180" : ""}`} />
                {showContext ? "Hide context" : "Add a little context (optional)"}
              </button>
              <AnimatePresence>
                {showContext && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <textarea
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder="Share as much or as little as you'd like…"
                      data-testid="mood-hero-context"
                      className="mt-4 w-full bg-ink-paper/60 border border-white/15 focus:border-gold-500/60 text-white placeholder:text-white/40 px-5 py-3.5 text-sm outline-none resize-none transition-colors rounded-none"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={busy}
                data-testid="start-transformation-btn"
                className="mt-8 group w-full sm:w-auto sm:min-w-[300px] flex items-center justify-center gap-3 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.16em] text-sm md:text-base px-10 py-4 md:py-4.5 transition-[background,transform,box-shadow] duration-300 disabled:opacity-70 active:scale-[0.98] shadow-[0_25px_70px_-20px_rgba(212,175,55,0.65)] hover:shadow-[0_30px_80px_-15px_rgba(212,175,55,0.8)]"
              >
                <Sparkles size={16} />
                {busy ? "Listening…" : "Start My Transformation"}
              </button>
              <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-white/40">
                Free · Takes 30 seconds · No sign-up needed
              </p>
            </motion.form>
          </div>
        </div>

        {/* Result — full width below the two-column hero for maximum readability */}
        <AnimatePresence mode="wait">
          {busy && (
            <motion.div
              key="busy"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-16 md:mt-20 max-w-3xl mx-auto border border-gold-500/20 bg-ink-paper/30 px-8 py-14 text-center"
              data-testid="mood-hero-busy"
            >
              <motion.div animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <HeartHandshake size={38} className="text-gold-500 mx-auto" strokeWidth={1.4} />
              </motion.div>
              <p className="mt-6 font-serif text-2xl md:text-3xl text-white">Bringing this before the Lord…</p>
            </motion.div>
          )}

          {!busy && result && (
            <motion.article
              key="result"
              ref={resultRef}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-16 md:mt-20 max-w-3xl mx-auto border border-gold-500/25 bg-gradient-to-br from-ink-paper/70 to-ink-royal/30 p-8 md:p-12"
              data-testid="mood-hero-result"
            >
              <div className="flex items-center gap-3">
                <HeartHandshake size={16} className="text-gold-500" />
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">A word for you</p>
              </div>
              <p className="mt-5 font-serif text-2xl md:text-4xl text-white leading-[1.15]" data-testid="mood-hero-affirmation">
                {result.affirmation}
              </p>

              <div className="mt-8 border-l-2 border-gold-500/60 pl-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gold-400 mb-2">{result.scripture_reference}</p>
                <p className="font-accent italic text-xl md:text-2xl text-white/90 leading-snug">"{result.scripture_text}"</p>
              </div>

              <div className="mt-8 space-y-4 text-white/80 leading-relaxed md:text-[16px]">
                {result.encouragement.split(/\n+/).map((p, i) => <p key={i}>{p.trim()}</p>)}
              </div>

              <div className="mt-8 border border-gold-500/25 bg-ink/60 p-6" data-testid="mood-hero-prayer">
                <div className="flex items-center gap-2 mb-3">
                  <Quote size={14} className="text-gold-500" />
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">Pray with me</p>
                </div>
                <p className="font-accent italic text-lg md:text-xl text-white/90 leading-relaxed">{result.prayer}</p>
              </div>

              {result.affirmations?.length > 0 && (
                <div className="mt-8">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-4">Declarations for today</p>
                  <ul className="space-y-2">
                    {result.affirmations.map((a, i) => (
                      <li key={i} className="border border-gold-500/15 bg-ink/40 px-5 py-3 text-white/85">
                        <span className="text-gold-500 mr-3">◆</span>{a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <button
                  onClick={reset}
                  data-testid="mood-hero-reset"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors"
                >
                  <RotateCcw size={13} /> Ask again
                </button>
                <Link
                  to="/studio"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-gold-300 transition-colors"
                >
                  Explore Kingdom Studio <ArrowUpRight size={13} />
                </Link>
              </div>
            </motion.article>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Welcome video message from Apostle Blaise Judah
// ---------------------------------------------------------------------------
function WelcomeMessage() {
  const [playing, setPlaying] = useState(false);

  const scrollToGuide = () => {
    const el = document.getElementById("mood-hero");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="welcome-message"
      data-testid="welcome-message"
      className="relative border-t border-gold-500/15 bg-gradient-to-b from-ink via-ink-royal/25 to-ink"
    >
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-28 md:py-40">
        {/* Overline */}
        <Reveal className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <Overline className="text-gold-400">A Welcome Message from</Overline>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-white">
            Apostle <span className="font-accent text-gold-gradient italic">Blaise Judah</span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
            A personal invitation to walk with us — and step into everything God has prepared for you.
          </p>
        </Reveal>

        {/* Video player placeholder */}
        <Reveal>
          <div className="relative mx-auto max-w-4xl">
            {/* Gold outline frame */}
            <div className="relative aspect-[16/9] border border-gold-500/30 shadow-[0_60px_120px_-40px_rgba(11,32,83,0.9)]">
              {/* Poster / placeholder image */}
              <img
                src={PORTRAIT}
                alt="Apostle Blaise Judah — welcome message"
                className={`absolute inset-0 w-full h-full object-cover object-[center_20%] transition-opacity duration-700 ${playing ? "opacity-0" : "opacity-100"}`}
                data-testid="welcome-video-poster"
              />
              {/* Subtle vignette + blue wash */}
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-ink-royal/20 pointer-events-none" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />

              {/* Bottom caption */}
              <div className="absolute left-6 right-6 bottom-5 md:left-8 md:bottom-7 flex items-end justify-between gap-4 z-10">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-gold-400 mb-2">Apostle Blaise Judah</p>
                  <p className="font-serif text-white text-xl md:text-2xl leading-tight max-w-xs md:max-w-sm">
                    "Welcome — this is your moment of transformation."
                  </p>
                </div>
                <span className="hidden md:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" /> 2 min · HD
                </span>
              </div>

              {/* Play button — centred */}
              {!playing && (
                <button
                  onClick={() => setPlaying(true)}
                  aria-label="Play welcome message"
                  data-testid="welcome-video-play"
                  className="absolute inset-0 flex items-center justify-center group focus:outline-none"
                >
                  <span className="relative flex items-center justify-center w-24 h-24 md:w-28 md:h-28">
                    {/* Pulsing gold ring */}
                    <span className="absolute inset-0 rounded-full bg-gold-500/15 animate-ping" />
                    <span className="absolute inset-0 rounded-full border border-gold-400/50" />
                    <span className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold-500 text-ink transition-transform duration-300 group-hover:scale-105 group-active:scale-95 shadow-[0_20px_60px_-15px_rgba(212,175,55,0.75)]">
                      <Play size={30} className="ml-1 fill-current" strokeWidth={0} />
                    </span>
                  </span>
                </button>
              )}

              {/* Placeholder "player" — swap src for real video anytime */}
              {playing && (
                <div className="absolute inset-0 bg-ink flex items-center justify-center text-center p-8" data-testid="welcome-video-player">
                  <div>
                    <div className="mx-auto w-14 h-14 rounded-full border border-gold-500/40 flex items-center justify-center mb-5">
                      <PlayCircle size={26} className="text-gold-400" strokeWidth={1.4} />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold-400 mb-3">Coming Soon</p>
                    <p className="font-serif text-white text-2xl md:text-3xl leading-tight max-w-md mx-auto">
                      Apostle's welcome video is being prepared.
                    </p>
                    <p className="mt-4 text-white/55 text-sm max-w-sm mx-auto">
                      Meanwhile, tap below to begin your transformation with the Mood-Responsive Guide.
                    </p>
                    <button
                      onClick={() => setPlaying(false)}
                      data-testid="welcome-video-close"
                      className="mt-8 text-[11px] uppercase tracking-[0.22em] text-white/50 hover:text-gold-300 transition-colors"
                    >
                      ← Back to poster
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Corner ornaments — kingdom crest touch */}
            <span className="absolute -top-3 -left-3 w-8 h-8 border-t border-l border-gold-500/60" />
            <span className="absolute -top-3 -right-3 w-8 h-8 border-t border-r border-gold-500/60" />
            <span className="absolute -bottom-3 -left-3 w-8 h-8 border-b border-l border-gold-500/60" />
            <span className="absolute -bottom-3 -right-3 w-8 h-8 border-b border-r border-gold-500/60" />
          </div>
        </Reveal>

        {/* Return to guide CTA */}
        <Reveal className="mt-14 md:mt-16 text-center">
          <button
            onClick={scrollToGuide}
            data-testid="return-to-guide-btn"
            className="group inline-flex items-center gap-3 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.18em] text-sm md:text-base px-10 md:px-14 py-4 md:py-5 transition-[background,transform,box-shadow] duration-300 active:scale-[0.98] shadow-[0_30px_80px_-25px_rgba(212,175,55,0.65)] hover:shadow-[0_40px_90px_-20px_rgba(212,175,55,0.85)]"
          >
            <ArrowUp size={17} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
            Return to the Guide
          </button>
          <p className="mt-5 text-[11px] uppercase tracking-[0.28em] text-white/40">
            Speak to your heart · Free · Takes 30 seconds
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <MoodHero />
      <WelcomeMessage />

      {/* Quotation band */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <Reveal className="max-w-4xl">
          <p className="font-accent italic text-3xl md:text-5xl lg:text-6xl leading-[1.15] text-white border-l-2 border-gold-500 pl-8 md:pl-12">
            "Whether you seek spiritual growth, leadership, business excellence, or technological
            advancement — you will find resources designed to help you{" "}
            <span className="text-gold-gradient not-italic">influence your generation.</span>"
          </p>
        </Reveal>
      </section>

      {/* Four pillars manifesto */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24" data-testid="pillars-section">
        <Reveal className="mb-16">
          <SectionHeading overline="Four Pillars of Impact" title={<>One calling.<br />Four dimensions of influence.</>} />
        </Reveal>
        <div className="grid gap-px bg-gold-500/15 border border-gold-500/15 md:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08}>
              <Link to={p.to} className="group flex h-full flex-col bg-ink p-8 lg:p-10 transition-colors duration-500 hover:bg-ink-paper">
                <span className="font-serif text-5xl text-gold-500/70">{p.n}</span>
                <h3 className="mt-8 font-serif text-3xl text-white">{p.title}</h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed flex-1">{p.desc}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-500 group-hover:gap-3 transition-all duration-300">
                  Explore <ArrowUpRight size={14} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee />

      {/* Quick CTAs strip */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16" data-testid="quick-ctas">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {CTAS.map((c, i) => (
            <Link
              key={c.label}
              to={c.to}
              data-testid={`quick-cta-${i}`}
              className="group inline-flex items-center justify-between gap-3 px-6 py-4 text-sm border border-gold-500/25 text-white/85 hover:border-gold-400 hover:text-gold-300 hover:bg-gold-500/5 transition-colors duration-300"
            >
              <span className="inline-flex items-center gap-3">
                <c.icon size={17} className="text-gold-400" />
                {c.label}
              </span>
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          ))}
        </div>
      </section>

      {/* Academy feature */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 grid gap-16 lg:grid-cols-2 lg:items-center" data-testid="academy-feature">
        <Reveal>
          <div className="corner-frame border border-gold-500/25 p-1">
            <img
              src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Judah AI Academy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Overline className="mb-5">Judah AI Academy</Overline>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-white">
            Equipping leaders for the <span className="font-accent text-gold-gradient italic">digital age.</span>
          </h2>
          <p className="mt-8 text-white/70 leading-relaxed max-w-lg">
            Practical Artificial Intelligence knowledge for ministries, businesses, educators, and
            students — enhancing productivity, innovation, and global competitiveness while
            maintaining biblical ethics and integrity.
          </p>
          <GoldRule className="my-8" />
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70">
            {["AI for Pastors", "AI for Entrepreneurs", "Prompt Engineering", "AI for Leadership"].map((c) => (
              <span key={c} className="flex items-center gap-2">
                <span className="h-1 w-1 bg-gold-500" /> {c}
              </span>
            ))}
          </div>
          <Link
            to="/academy"
            data-testid="academy-feature-cta"
            className="mt-10 group inline-flex items-center gap-2.5 bg-gold-500 px-7 py-3.5 text-sm font-semibold text-ink hover:bg-gold-400 active:scale-[0.98] transition-[background,transform] duration-300"
          >
            Browse Courses
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>

      {/* Partners trust row */}
      <section className="border-t border-gold-500/20 py-16" data-testid="home-partners-strip">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-center text-[11px] uppercase tracking-[0.35em] text-gold-500 mb-10">In partnership with</p>
          <div className="flex flex-wrap items-center justify-center gap-5">
            {[
              { name: "African Institute of AI & Entrepreneurship", logo: "/partners/aiaie.png", url: "https://aiaie.africa" },
            ].map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-partner-aiaie"
                aria-label={p.name}
                className="rounded-md bg-white px-8 py-5 grayscale hover:grayscale-0 transition-[filter,transform] duration-500 hover:scale-[1.03]"
              >
                <img src={p.logo} alt={p.name} className="h-11 w-auto object-contain" />
              </a>
            ))}
            <Link
              to="/contact"
              data-testid="become-partner"
              className="flex items-center gap-2 rounded-md border border-dashed border-gold-500/40 px-8 py-6 text-xs uppercase tracking-[0.2em] text-white/60 hover:border-gold-400 hover:text-gold-300 transition-colors duration-300"
            >
              Become a Partner <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stories of Faith */}
      <HomeStoriesOfFaith />

      {/* Closing CTA */}
      <section className="relative border-t border-gold-500/20 py-28 md:py-40 text-center" data-testid="home-closing">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <Overline className="mb-6">Let's build the Kingdom together</Overline>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-white">
              Partner, learn, or invite Apostle Blaise Judah to your table.
            </h2>
            <div className="mt-12 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="bg-gold-500 px-8 py-4 text-sm font-semibold text-ink hover:bg-gold-400 active:scale-[0.98] transition-[background,transform] duration-300">
                Get in Touch
              </Link>
              <Link to="/about" className="border border-gold-500/40 px-8 py-4 text-sm text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors duration-300">
                Read My Story
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Newsletter */}
      <section className="border-t border-gold-500/20 py-24 md:py-32" data-testid="home-newsletter">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <Reveal>
            <NewsletterSignup />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
